import type { Database } from '@/integrations/supabase/types';

export type AcquisitionSource = Database['public']['Enums']['acquisition_source'];

export interface AcquisitionOption {
  value: AcquisitionSource;
  emoji: string;
  label: string;
}

export const ACQUISITION_OPTIONS: AcquisitionOption[] = [
  { value: 'mentorship', emoji: '🎓', label: 'Soy alumno de la mentoría de Erick Meoño' },
  { value: 'social', emoji: '📱', label: 'Llegué por Instagram o TikTok' },
  { value: 'referral', emoji: '👥', label: 'Me lo recomendó alguien' },
];

/**
 * Steps shown in the reduced onboarding tour (Cambio 2). The rest of the steps
 * stay available in the full "Cómo usar FOCO" guide (help button).
 */
export const ONBOARDING_TOUR_STEP_IDS = ['welcome', 'score', 'transactions', 'ai-advisor'] as const;

/** Alumnos de mentoría saltan el tour educativo y van directo a activación. */
export function shouldSkipTour(source: AcquisitionSource | null | undefined): boolean {
  return source === 'mentorship';
}

const LEGACY_GUIDE_KEY = 'foco-guide-completed-v1';
const LEGACY_SKIP_KEY = 'foco-guide-skip';
const ONBOARDING_LOCAL_DONE_KEY = 'foco-onboarding-done-v1';

export function markOnboardingDoneLocal(): void {
  try {
    localStorage.setItem(ONBOARDING_LOCAL_DONE_KEY, 'true');
  } catch {
    /* ignore */
  }
}

export function isOnboardingDoneLocal(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_LOCAL_DONE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Legacy users who already completed/skipped the old guide should not be re-onboarded. */
export function hasLegacyGuideFlag(): boolean {
  try {
    return (
      localStorage.getItem(LEGACY_GUIDE_KEY) === 'true' ||
      localStorage.getItem(LEGACY_SKIP_KEY) === 'true'
    );
  } catch {
    return false;
  }
}

export interface OnboardingGateInput {
  onboardingCompletedAt: string | null | undefined;
  hasAnyTransactions: boolean;
  isLoadingTransactions: boolean;
}

/**
 * Only genuinely new users see the activation flow: onboarding not completed,
 * no legacy guide flag, and no existing transactions. This prevents disrupting
 * existing accounts when the feature ships.
 */
export function shouldStartOnboarding({
  onboardingCompletedAt,
  hasAnyTransactions,
  isLoadingTransactions,
}: OnboardingGateInput): boolean {
  if (onboardingCompletedAt) return false;
  if (isOnboardingDoneLocal()) return false;
  if (hasLegacyGuideFlag()) return false;
  if (isLoadingTransactions) return false;
  if (hasAnyTransactions) return false;
  return true;
}

export interface WowMessage {
  title: string;
  message: string;
}

/** Dynamic message shown on the WOW screen, keyed by score band (Cambio 4). */
export function getWowMessage(score: number): WowMessage {
  const title = `Tu FOCO Score inicial es ${score}`;
  if (score <= 40) {
    return {
      title,
      message:
        'Estás en Zona de Construcción. La buena noticia: desde aquí cualquier mejora se siente de inmediato. La IA ya identificó tu primer paso.',
    };
  }
  if (score <= 60) {
    return {
      title,
      message:
        'Zona de Avance. Tienes una base sólida, pero hay fugas que podemos cerrar. La IA detectó dónde.',
    };
  }
  if (score <= 80) {
    return {
      title,
      message:
        'Zona de Avance Fuerte. Estás mejor que la mayoría. Vamos a llevar esto a Zona de Libertad.',
    };
  }
  return {
    title,
    message:
      'Zona de Libertad. Salud financiera sólida. Vamos a optimizar y proteger lo que ya construiste.',
  };
}
